import { useMemo, useState } from 'react';
import { AlertTriangle, ChevronDown, Clock, PauseCircle, RotateCcw, ShieldCheck, Trash2, VideoIcon, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { apiClient } from '../lib/apiClient';
import { toast } from '../lib/toast';
import { ConfirmDialog } from './ConfirmDialog';
import DeletionHistory, { BIN_STYLES, Badge, Centered, RetryButton, SearchBox, border, formatSize, muted } from './DeletionHistory';
import { BIN_RETENTION_DAYS, forEachVideo, restoreVideos, useRecycleBin, type BinnedVideo } from '../hooks/useRecycleBin';
import { broadcastVideosRefresh } from '../hooks/useVideos';
import { useArchiveAccess } from '../hooks/useArchiveAccess';

type Sort = 'deleted' | 'title' | 'size';
const DAY = 86_400_000;

const daysLeft = (purgeAt: string) => Math.max(0, Math.ceil((new Date(purgeAt).getTime() - Date.now()) / DAY));
const sizeOf = (v: BinnedVideo) => Number(v.fileSize) || 0;

const ghostButton = (color: string): React.CSSProperties => ({
  display: 'inline-flex', alignItems: 'center', gap: 6, padding: '6px 12px', borderRadius: 8,
  fontSize: 13, fontWeight: 600, cursor: 'pointer', background: 'transparent',
  border: `1px solid color-mix(in srgb, ${color} 35%, transparent)`, color, whiteSpace: 'nowrap',
});

/**
 * Recycle Bin (left navigation): deleted videos stay restorable for 30 days, then the server
 * deletes them permanently. Second tab: the history of deletions, restores and permanent deletions.
 */
export default function RecycleBinPage() {
  const { t } = useTranslation();
  const [tab, setTab] = useState<'videos' | 'history'>('videos');
  const { videos, failed, reload } = useRecycleBin();
  const [query, setQuery] = useState('');
  const [sort, setSort] = useState<Sort>('deleted');
  const [selected, setSelected] = useState<Set<string>>(new Set());
  // Videos with an action running (their buttons are disabled meanwhile).
  const [busy, setBusy] = useState<Set<string>>(new Set());
  const [confirm, setConfirm] = useState<{ kind: 'forever'; ids: string[] } | { kind: 'empty' } | null>(null);
  // Admin Center Archive Mode: read-only allows neither restoring nor deleting; Legal Hold stops
  // permanent deletion (every role); either one, or a school not Active, pauses the 30 days.
  const archive = useArchiveAccess();
  const canRestore = archive.mode === 'full';
  const canDestroy = canRestore && !archive.legalHold;
  const schoolPaused = archive.legalHold || (!!archive.status && archive.status !== 'Active');
  const canSelect = canRestore || canDestroy;

  const shown = useMemo(() => {
    const q = query.trim().toLowerCase();
    const list = (videos ?? []).filter((v) => !q || (v.title || '').toLowerCase().includes(q));
    return [...list].sort((a, b) =>
      sort === 'title' ? (a.title || '').localeCompare(b.title || '')
        : sort === 'size' ? sizeOf(b) - sizeOf(a)
        : b.deletedAt.localeCompare(a.deletedAt));
  }, [videos, query, sort]);

  // Only videos still in the bin count (a refresh may have removed some).
  const selectedIds = shown.filter((v) => selected.has(v.id)).map((v) => v.id);
  const allSelected = shown.length > 0 && selectedIds.length === shown.length;
  const toggle = (id: string) => setSelected((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  const run = async (ids: string[], action: 'restore' | 'forever') => {
    setBusy((prev) => new Set([...prev, ...ids]));
    let mp4Busy = false;
    let legalHold = false;
    const failedIds = action === 'restore'
      ? await restoreVideos(ids)
      : await forEachVideo(ids, async (id) => {
        const result = await apiClient.deleteVideoPermanently(id);
        const code = (result.error as { code?: string } | null)?.code;
        if (code === 'MP4_IN_PROGRESS') mp4Busy = true;
        if (code === 'LEGAL_HOLD') legalHold = true;
        return result;
      });
    setBusy((prev) => { const next = new Set(prev); ids.forEach((id) => next.delete(id)); return next; });
    setSelected(new Set(failedIds)); // the failed ones stay selected for another try
    const done = ids.length - failedIds.length;
    if (done) toast.success(t(action === 'restore' ? 'recycle_bin.restored' : 'recycle_bin.deleted_forever', { count: done }));
    if (failedIds.length) {
      toast.error(legalHold
        ? t('archive.legal_hold_refused', "Legal Hold is on: videos can't be deleted permanently.")
        : mp4Busy
        ? t('recycle_bin.mp4_busy', 'An MP4 of this video is still being made. Try again in a few minutes.')
        : t(action === 'restore' ? 'recycle_bin.restore_failed' : 'recycle_bin.delete_failed',
          action === 'restore' ? 'Some videos could not be restored. Try again from the Recycle bin.' : 'Some videos could not be deleted. Try again.'));
    }
  };

  const emptyBin = async () => {
    const { data, error } = await apiClient.emptyRecycleBin();
    broadcastVideosRefresh();
    setSelected(new Set());
    if (error) {
      toast.error((error as { code?: string }).code === 'LEGAL_HOLD'
        ? t('archive.legal_hold_refused', "Legal Hold is on: videos can't be deleted permanently.")
        : t('recycle_bin.delete_failed', 'Some videos could not be deleted. Try again.'));
    }
    else toast.success(t('recycle_bin.deleted_forever', { count: data?.removed ?? 0 }));
  };

  const confirmVideos = confirm?.kind === 'forever' ? (videos ?? []).filter((v) => confirm.ids.includes(v.id)) : [];
  const confirmSize = (confirm?.kind === 'empty' ? videos ?? [] : confirmVideos).reduce((sum, v) => sum + sizeOf(v), 0);

  const renderRow = (v: BinnedVideo) => {
    const left = daysLeft(v.purgeAt);
    const paused = schoolPaused || !!v.binPausedAt;
    const soon = !paused && left <= 3;
    const isBusy = busy.has(v.id);
    return (
      <li key={v.id} className="dh-row" style={{
        display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap', padding: '12px 12px', borderRadius: 12,
        opacity: isBusy ? 0.55 : 1,
      }}>
        {canSelect && <input
          type="checkbox"
          checked={selected.has(v.id)}
          onChange={() => toggle(v.id)}
          disabled={isBusy}
          aria-label={t('recycle_bin.select', 'Select {{title}}', { title: v.title, interpolation: { escapeValue: false } })}
          style={{ width: 16, height: 16, accentColor: 'var(--theme-primary, #4f46e5)', flexShrink: 0, cursor: 'pointer' }}
        />}
        <div style={{
          width: 'clamp(72px, 22vw, 112px)', aspectRatio: '16 / 9', borderRadius: 8, overflow: 'hidden', flexShrink: 0,
          background: 'color-mix(in srgb, var(--text-secondary, #6b7280) 12%, transparent)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', color: muted,
        }}>
          {v.thumbnailUrl
            ? <img src={v.thumbnailUrl} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', filter: 'grayscale(35%)' }} />
            : <VideoIcon size={22} aria-hidden />}
        </div>
        <div style={{ flex: '1 1 140px', minWidth: 0 }}>
          <div style={{ fontSize: 14, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {v.title || t('deletion_history.untitled', 'Untitled video')}
          </div>
          <div style={{ fontSize: 12, color: muted, marginTop: 3 }}>
            {t('recycle_bin.deleted_on', 'Deleted {{date}}', { date: new Date(v.deletedAt).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' }) })}
            {sizeOf(v) > 0 && ` · ${formatSize(sizeOf(v))}`}
          </div>
          <span title={paused ? t('archive.paused_hint', 'The 30-day countdown is paused. It resumes when your school is active again and Legal Hold is off.') : t('recycle_bin.purge_on', 'Deleted permanently on {{date}}', { date: new Date(v.purgeAt).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' }) })} style={{
            display: 'inline-flex', alignItems: 'center', gap: 4, marginTop: 6, padding: '2px 8px', borderRadius: 999,
            fontSize: 11, fontWeight: 600,
            background: soon ? 'color-mix(in srgb, var(--theme-warning, #d97706) 14%, transparent)' : 'color-mix(in srgb, var(--text-secondary, #6b7280) 10%, transparent)',
            color: soon ? 'var(--theme-warning, #b45309)' : muted,
          }}>
            {paused ? <PauseCircle size={11} aria-hidden /> : <Clock size={11} aria-hidden />}
            {paused ? t('archive.paused', 'Paused')
              : left === 0 ? t('recycle_bin.days_left_today', 'Last day') : t('recycle_bin.days_left', { count: left })}
          </span>
        </div>
        <div style={{ display: 'flex', gap: 8, marginLeft: 'auto' }}>
          {canRestore && <button type="button" disabled={isBusy} onClick={() => void run([v.id], 'restore')}
            style={{ ...ghostButton('var(--primary-text, #4f46e5)'), cursor: isBusy ? 'default' : 'pointer' }}>
            <RotateCcw size={14} aria-hidden /> {t('recycle_bin.restore', 'Restore')}
          </button>}
          {canDestroy && <button type="button" disabled={isBusy} onClick={() => setConfirm({ kind: 'forever', ids: [v.id] })}
            title={t('recycle_bin.delete_forever', 'Delete permanently')}
            aria-label={t('recycle_bin.delete_forever', 'Delete permanently')}
            style={{ ...ghostButton('var(--theme-error, #dc2626)'), padding: '6px 9px', cursor: isBusy ? 'default' : 'pointer' }}>
            <Trash2 size={14} aria-hidden />
          </button>}
        </div>
      </li>
    );
  };

  const tabButton = (id: 'videos' | 'history', label: string) => (
    <button
      key={id}
      type="button"
      role="tab"
      aria-selected={tab === id}
      onClick={() => setTab(id)}
      className="px-4 py-2 font-medium text-sm border-b-2 transition-colors"
      style={{
        borderColor: tab === id ? 'var(--dropdown-item-selected-bg, var(--button-primary-bg, var(--theme-primary, #4f46e5)))' : 'transparent',
        backgroundColor: tab === id ? 'var(--dropdown-item-selected-bg, var(--button-primary-bg, var(--theme-primary, #4f46e5)))' : 'transparent',
        color: tab === id ? 'var(--dropdown-item-selected-foreground, var(--button-primary-foreground, #ffffff))' : 'var(--text-primary, var(--theme-foreground, #111827))',
        borderTopLeftRadius: 6, borderTopRightRadius: 6,
      }}
    >
      {label}
    </button>
  );

  return (
    <div className="flex-1 p-6" style={{ backgroundColor: 'var(--bg-primary, var(--theme-background, #f9fafb))', color: 'var(--text-primary, var(--theme-foreground, #111827))' }}>
      <style>{BIN_STYLES}</style>
      <div className="max-w-5xl mx-auto">
        <div className="mb-6">
          <h1 className="text-2xl font-bold">{t('recycle_bin.title', 'Recycle bin')}</h1>
          <p className="text-sm" style={{ color: muted, marginTop: 4 }}>
            {t('recycle_bin.subtitle', 'Deleted videos stay here for {{days}} days, then they are deleted permanently. They still count toward your storage until then.', { days: BIN_RETENTION_DAYS })}
          </p>
        </div>

        <div role="tablist" className="flex border-b mb-4" style={{ borderColor: 'var(--border-color, var(--theme-border, #d6d6d6))' }}>
          {tabButton('videos', videos?.length ? `${t('recycle_bin.tab_videos', 'Deleted videos')} (${videos.length})` : t('recycle_bin.tab_videos', 'Deleted videos'))}
          {tabButton('history', t('recycle_bin.tab_history', 'History'))}
        </div>

        {tab === 'history' ? <DeletionHistory /> : (
          <>
            {schoolPaused && (
              <div role="status" style={{
                display: 'flex', alignItems: 'flex-start', gap: 10, padding: '10px 14px', marginBottom: 12, borderRadius: 10, fontSize: 13,
                background: 'var(--surfaceSelected, rgba(79,70,229,0.08))', color: 'var(--text-primary, #111827)',
              }}>
                <ShieldCheck size={16} aria-hidden style={{ flexShrink: 0, marginTop: 1, color: 'var(--primary-text, #4f46e5)' }} />
                <span>
                  {archive.legalHold
                    ? t('archive.legal_hold_note', "Legal Hold is on: videos here are kept and can't be deleted permanently. The 30-day countdown is paused.")
                    : t('archive.inactive_note', "Your school's account isn't active: videos here are kept and the 30-day countdown is paused.")}
                </span>
              </div>
            )}
            {/* Toolbar: search, sort, empty — or the selection actions */}
            {videos && videos.length > 0 && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 8, minHeight: 38 }}>
                {canSelect && <input
                  type="checkbox"
                  checked={allSelected}
                  onChange={() => setSelected(allSelected ? new Set() : new Set(shown.map((v) => v.id)))}
                  aria-label={t('recycle_bin.select_all', 'Select all')}
                  title={t('recycle_bin.select_all', 'Select all')}
                  style={{ width: 16, height: 16, marginLeft: 12, accentColor: 'var(--theme-primary, #4f46e5)', cursor: 'pointer' }}
                />}
                {selectedIds.length > 0 ? (
                  <>
                    <span style={{ fontSize: 13, fontWeight: 600 }}>{t('recycle_bin.selected', '{{count}} selected', { count: selectedIds.length })}</span>
                    {canRestore && <button type="button" onClick={() => void run(selectedIds, 'restore')} style={ghostButton('var(--primary-text, #4f46e5)')}>
                      <RotateCcw size={14} aria-hidden /> {t('recycle_bin.restore', 'Restore')}
                    </button>}
                    {canDestroy && <button type="button" onClick={() => setConfirm({ kind: 'forever', ids: selectedIds })} style={ghostButton('var(--theme-error, #dc2626)')}>
                      <Trash2 size={14} aria-hidden /> {t('recycle_bin.delete_forever', 'Delete permanently')}
                    </button>}
                    <button type="button" onClick={() => setSelected(new Set())} title={t('recycle_bin.clear', 'Clear selection')}
                      aria-label={t('recycle_bin.clear', 'Clear selection')}
                      style={{ display: 'inline-flex', padding: 6, border: 'none', background: 'transparent', color: muted, cursor: 'pointer' }}>
                      <X size={16} aria-hidden />
                    </button>
                  </>
                ) : (
                  <>
                    <div style={{ flex: '1 1 220px', maxWidth: 320 }}>
                      <SearchBox value={query} onChange={setQuery} placeholder={t('recycle_bin.search', 'Search deleted videos')} />
                    </div>
                    <div style={{ position: 'relative' }}>
                      <select
                        value={sort}
                        onChange={(e) => setSort(e.target.value as Sort)}
                        aria-label={t('library_sort.label', 'Sort')}
                        style={{
                          appearance: 'none', padding: '7px 30px 7px 12px', borderRadius: 10, fontSize: 13, cursor: 'pointer',
                          border: `1px solid ${border}`, background: 'var(--dropdown-bg, var(--card-bg, transparent))', color: 'inherit',
                        }}
                      >
                        <option value="deleted">{t('recycle_bin.sort_deleted', 'Recently deleted')}</option>
                        <option value="title">{t('recycle_bin.sort_title', 'Title')}</option>
                        <option value="size">{t('recycle_bin.sort_size', 'Size')}</option>
                      </select>
                      <ChevronDown size={14} aria-hidden style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none', color: muted }} />
                    </div>
                    {canDestroy && <button type="button" onClick={() => setConfirm({ kind: 'empty' })}
                      style={{ ...ghostButton('var(--theme-error, #dc2626)'), marginLeft: 'auto' }}>
                      <Trash2 size={14} aria-hidden /> {t('recycle_bin.empty_bin', 'Empty recycle bin')}
                    </button>}
                  </>
                )}
              </div>
            )}

            {failed && videos === null ? (
              <Centered>
                <Badge><AlertTriangle size={22} /></Badge>
                <p style={{ margin: 0, fontSize: 14, fontWeight: 600 }}>{t('recycle_bin.load_failed', 'Could not load the Recycle bin.')}</p>
                <RetryButton onClick={() => void reload()} />
              </Centered>
            ) : videos === null ? (
              <ul aria-busy="true" aria-label={t('loading', 'Loading…')} style={{ listStyle: 'none', margin: 0, padding: 0 }}>
                {[0, 1, 2].map((i) => (
                  <li key={i} style={{ display: 'flex', gap: 14, alignItems: 'center', padding: '12px' }}>
                    <span className="dh-skeleton" style={{ width: 112, height: 63, borderRadius: 8 }} />
                    <span style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 8 }}>
                      <span className="dh-skeleton" style={{ height: 12, width: `${55 - i * 10}%` }} />
                      <span className="dh-skeleton" style={{ height: 10, width: '30%' }} />
                    </span>
                  </li>
                ))}
              </ul>
            ) : videos.length === 0 ? (
              <Centered>
                <Badge><Trash2 size={22} /></Badge>
                <p style={{ margin: 0, fontSize: 15, fontWeight: 600 }}>{t('recycle_bin.empty_title', 'The Recycle bin is empty')}</p>
                <p style={{ margin: 0, fontSize: 13, color: muted, maxWidth: 380 }}>
                  {t('recycle_bin.empty_hint', 'Videos you delete stay here for {{days}} days, so you can restore them.', { days: BIN_RETENTION_DAYS })}
                </p>
              </Centered>
            ) : shown.length === 0 ? (
              <Centered>
                <p style={{ margin: 0, fontSize: 13, color: muted }}>{t('recycle_bin.no_matches', 'No deleted videos match your search.')}</p>
              </Centered>
            ) : (
              <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: 2 }}>{shown.map(renderRow)}</ul>
            )}
          </>
        )}
      </div>

      {confirm && (
        <ConfirmDialog
          danger
          title={confirm.kind === 'empty'
            ? t('recycle_bin.confirm_empty_title', 'Empty the Recycle bin?')
            : t('recycle_bin.confirm_forever_title', { count: confirm.ids.length })}
          message={
            <>
              {confirm.kind === 'empty'
                ? t('recycle_bin.confirm_empty_message', { count: videos?.length ?? 0 })
                : confirmVideos.length === 1
                  ? t('recycle_bin.confirm_forever_one_message', '"{{title}}" is deleted for good, with its comments, reactions and views. This can\'t be undone.', { title: confirmVideos[0].title, interpolation: { escapeValue: false } })
                  : t('recycle_bin.confirm_forever_message', 'They are deleted for good, with their comments, reactions and views. This can\'t be undone.')}
              {confirmSize > 0 && (
                <span style={{ display: 'block', marginTop: 6, color: 'var(--theme-success, #16a34a)' }}>
                  {t('video_delete.frees', 'Frees {{size}} of your storage.', { size: formatSize(confirmSize) })}
                </span>
              )}
            </>
          }
          confirmLabel={confirm.kind === 'empty' ? t('recycle_bin.empty_bin', 'Empty recycle bin') : t('recycle_bin.delete_forever', 'Delete permanently')}
          onCancel={() => setConfirm(null)}
          // The dialog stays open (with its spinner) until the deletion finishes.
          onConfirm={async () => {
            if (confirm.kind === 'empty') await emptyBin();
            else await run(confirm.ids, 'forever');
            setConfirm(null);
          }}
        />
      )}
    </div>
  );
}
